// Visit status machine (ТЗ §7.2, §12).

import type { CheckIssue, Diagnosis, VisitStatus } from "./types.ts";

export const VISIT_STATUS_LABEL: Record<VisitStatus, string> = {
  draft: "Черновик",
  recording: "Идёт запись",
  uploading: "Загружается",
  recognizing: "Распознаётся",
  recognition_failed: "Ошибка распознавания",
  needs_review: "Требует проверки",
  ready: "Готов",
  completed: "Завершён",
  transferred: "Перенесён в МИС",
};

export type VisitAction =
  | "start_recording"
  | "finish_recording"
  | "all_segments_received"
  | "recognized"
  | "recognition_failed"
  | "rerecognize"
  | "addendum"
  | "review_changed"
  | "complete"
  | "mark_transferred"
  | "amend";

const ALLOWED: Record<VisitAction, VisitStatus[]> = {
  start_recording: ["draft"],
  finish_recording: ["recording"],
  all_segments_received: ["uploading"],
  recognized: ["recognizing"],
  recognition_failed: ["recognizing"],
  rerecognize: ["recognition_failed", "needs_review", "ready"],
  addendum: ["needs_review", "ready", "recognition_failed"],
  review_changed: ["needs_review", "ready"],
  complete: ["ready"],
  mark_transferred: ["completed"],
  amend: ["completed", "transferred"],
};

export function canDo(status: VisitStatus, action: VisitAction): boolean {
  return ALLOWED[action].includes(status);
}

export class TransitionError extends Error {
  constructor(
    public status: VisitStatus,
    public action: VisitAction,
  ) {
    super(`Действие недоступно в статусе «${VISIT_STATUS_LABEL[status]}»`);
    this.name = "TransitionError";
  }
}

export function assertCan(status: VisitStatus, action: VisitAction): void {
  if (!canDo(status, action)) throw new TransitionError(status, action);
}

/** Whether the visit meets «Готов»: one main diagnosis with accepted code and no unconfirmed errors. */
export function reviewReadiness(diagnoses: Diagnosis[], issues: CheckIssue[]): { ready: boolean; reasons: string[] } {
  const reasons: string[] = [];
  const mains = diagnoses.filter((d) => d.role === "main" && d.state !== "rejected");
  const acceptedMains = mains.filter((d) => d.state === "accepted" && d.code);
  if (acceptedMains.length === 0) reasons.push("Нет основного диагноза с принятым кодом");
  if (acceptedMains.length > 1) reasons.push("Основной диагноз должен быть один");
  const errors = issues.filter((i) => i.level === "error" && i.status === "active");
  if (errors.length) reasons.push(`Ошибки проверки кодов: ${[...new Set(errors.map((e) => e.check))].join(", ")}`);
  return { ready: reasons.length === 0, reasons };
}

export function reviewStatus(diagnoses: Diagnosis[], issues: CheckIssue[]): "ready" | "needs_review" {
  return reviewReadiness(diagnoses, issues).ready ? "ready" : "needs_review";
}

export const EDITABLE_STATUSES: VisitStatus[] = ["needs_review", "ready"];
export const RECORDABLE_STATUSES: VisitStatus[] = ["draft", "recording", "needs_review", "ready", "recognition_failed"];
